import React from "react";

type ButtonVariant = "filled" | "outline" | "text" | "success" | "danger";
type ButtonSize = "small" | "medium" | "large" | "none";
type ButtonType = "submit" | "reset" | "button";

interface ButtonProps {
  label?: string;
  ariaLabel?: string;
  "aria-current"?: "page";
  className?: string;
  onClick?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  children?: React.ReactNode;
  buttonType?: ButtonType;
}

const Button: React.FC<ButtonProps> = ({
  label,
  ariaLabel,
  "aria-current": ariaCurrent,
  className = "",
  onClick,
  variant = "filled",
  size = "large",
  disabled = false,
  startIcon,
  endIcon,
  children,
  buttonType = "button",
}) => {
  const baseStyle =
    "rounded-lg font-semibold transition duration-300 flex items-center justify-center gap-2";

  const variantStyles: Record<ButtonVariant, string> = {
    filled: "bg-primary text-textPrimary hover:bg-hover-green",
    outline: "border border-primary text-gray-700 hover:bg-hover-green",
    text: "bg-none text-primary hover:underline",
    success: "bg-success text-white hover:bg-green-600",
    danger: "bg-danger text-white hover:bg-red-600",
  };

  const disabledStyles: Record<ButtonVariant, string> = {
    filled:
      "opacity-50 !bg-[#D0D0D0] border-none hover:bg-[#D0D0D0] cursor-not-allowed !text-[#939DB0]",
    outline: "opacity-50 cursor-not-allowed text-[#939DB0]",
    text: "opacity-50 cursor-not-allowed !bg-white", // No background
    success: "opacity-50 !bg-[#D0D0D0] cursor-not-allowed text-[#939DB0]",
    danger: "opacity-50 !bg-[#D0D0D0] cursor-not-allowed text-[#939DB0]",
  };

  const sizeStyles: Record<ButtonSize, string> = {
    small: "px-3 py-2 text-p",
    medium: "px-4 py-3 py-2 text-h6",
    large: "px-6 py-3.5 text-h6",
    none: "",
  };

  return (
    <button
      className={` flex items-center
    ${baseStyle}
    ${variantStyles[variant]}
    ${sizeStyles[size]}
    ${disabled ? disabledStyles[variant] : ""}
    ${className}
  `}
      aria-label={ariaLabel}
      aria-current={ariaCurrent}
      onClick={onClick}
      disabled={disabled}
      type={buttonType}
    >
      {startIcon && <span>{startIcon}</span>}
      {children ? children : <span className="flex leading-4">{label}</span>}
      {endIcon && <span>{endIcon}</span>}
    </button>
  );
};

export default Button;
